import { useCallback, useEffect, useRef, useState } from 'react';

export type CopyState = 'idle' | 'copied' | 'failed';

/** Copies `text` to the clipboard; `false` when the browser refuses (insecure context, permission). */
export async function copyText(text: string): Promise<boolean> {
    try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // Fall through: the caller tells the user to copy by hand.
    }
    return false;
}

/** Copy-to-clipboard with a 2 s "Copied" state (for the button text and an `aria-live` line). */
export function useCopy(text: string): [CopyState, () => void] {
    const [state, setState] = useState<CopyState>('idle');
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => () => clearTimeout(timer.current), []);

    const copy = useCallback(() => {
        void copyText(text).then(ok => {
            setState(ok ? 'copied' : 'failed');
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setState('idle'), 2000);
        });
    }, [text]);

    return [state, copy];
}
